import React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Stack,
  TextField,
  TextareaAutosize,
  Typography,
} from "@mui/material";
import { AccessTime as ClockIcon } from "@mui/icons-material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import StarRating from "./StarRating.tsx";
import { FieldError } from "../ScoreCardValidation/ValidationErrors.tsx";
import type { ValidationError } from "../ScoreCardValidation/ValidationSchema.tsx";

interface RateYourExperienceProps {
  date: Date | null;
  ratings: any;
  starRatings: any;
  staffMemberDesc: any;
  setStaffMemberDesc: any;
  briefJson: any;
  addOnQuestions: any;
  addOnQuestionsData: any;
  textInputs: Record<string, string>;
  reportDetailsHeader: any;
  reportOtherDetails: any;
  setDate: (newDate: Date | null) => void;
  onRatingChange: (service: string, value: number) => void;
  onTextChange: (field: string, value: string) => void;
  onAddOnQuestionChange: (field: string, value: string) => void;
  validationErrors?: ValidationError[];
}

const monthsData = [
  { id: 1, label: "January", value: "january" },
  { id: 2, label: "February", value: "february" },
  { id: 3, label: "March", value: "march" },
  { id: 4, label: "April", value: "april" },
  { id: 5, label: "May", value: "may" },
  { id: 6, label: "June", value: "june" },
  { id: 7, label: "July", value: "july" },
  { id: 8, label: "August", value: "august" },
  { id: 9, label: "September", value: "september" },
  { id: 10, label: "October", value: "october" },
  { id: 11, label: "November", value: "november" },
  { id: 12, label: "December", value: "december" },
];

const RateYourExperience: React.FC<RateYourExperienceProps> = ({
  date,
  ratings,
  staffMemberDesc,
  setStaffMemberDesc,
  starRatings,
  textInputs,
  briefJson,
  addOnQuestions,
  addOnQuestionsData,
  reportDetailsHeader,
  reportOtherDetails,
  setDate,
  onRatingChange,
  onTextChange,
  onAddOnQuestionChange,
  validationErrors = [],
}) => {
  console.log(addOnQuestions);
  console.log(textInputs);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [timeOfVisit, setTimeOfVisit] = useState("");

  const handleChange = (event) => {
    setSelectedMonths(event.target.value);
  };

  useEffect(() => {
    if (reportDetailsHeader) {
      setSelectedMonths(reportDetailsHeader.reportMonth);
      const visitDate = date;
      const hours = visitDate?.getHours().toString().padStart(2, "0"); // Ensure two digits
      const minutes = visitDate?.getMinutes().toString().padStart(2, "0"); // Ensure two digits
      setTimeOfVisit(`${hours}:${minutes}`); // Set formatted time
    }
    //setDate(date)
  }, [reportDetailsHeader]);

  return (
    <Stack spacing={4}>
      {reportOtherDetails?.reportMessage && (
        <Box
          sx={{
            p: 2,
            border: "1px solid #ccc",
            borderRadius: 1,
            fontSize: "0.875rem",
            fontFamily: "Roboto, sans-serif",
            whiteSpace: "pre-wrap",
          }}
          dangerouslySetInnerHTML={{ __html: reportOtherDetails.reportMessage }}
        />
      )}
      <Box>
        <Typography variant="h5" fontWeight={600}>
          Rate Your Experience
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Give star Rating out of 5. 1 is lowest and 5 is Highest.
        </Typography>
      </Box>

      <Grid container spacing={3} style={{ gap: "35px" }}>
        {starRatings.map((starRating) => (
          <Grid
            item
            className="starRatingBox"
            style={{ maxWidth: "300px" }}
            xs={12}
            md={4}
            key={starRating.reportStarRatingId}
          >
            <StarRating
              key={starRating.reportStarRatingId}
              service={starRating.reportStarRatingId.toString()} // Ensure key is unique
              label={starRating.starRatingDesc}
              rating={
                ratings[starRating.reportStarRatingId] ||
                starRating.starRatingScore
              } // Ensure updated rating is passed
              required={true}
              onRatingChange={onRatingChange}
              error={validationErrors.some(
                (e) => e.field === `rating_${starRating.reportStarRatingId}`
              )}
            />
            <FieldError
              errors={validationErrors}
              field={`rating_${starRating.reportStarRatingId}`}
            />
          </Grid>
        ))}
      </Grid>

      <Card sx={{ backgroundColor: "inherit" }}>
        <Typography variant="h5" fontWeight={600}>
          Score Card
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Do not use your Membership Card. Your movement may be tracked by CCTV.
        </Typography>
        <Stack spacing={4} sx={{ backgroundColor: "inherit" }}>
          <Card sx={{ backgroundColor: "inherit" }}>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4} className="scoreCardBox">
                  <FormControl fullWidth>
                    <InputLabel shrink={false}>
                      Mystery Shop Month{" "}
                      <span style={{ color: "#D32F2F" }}> *</span>
                    </InputLabel>
                    <TextField
                      select
                      // label="Mystery Shop Month*"
                      value={selectedMonths}
                      onChange={handleChange}
                      fullWidth
                    >
                      {monthsData.map((month) => (
                        <MenuItem key={month.id} value={month.id}>
                          {month.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <FormControl
                      fullWidth
                      error={validationErrors.some((e) => e.field === "date")}
                      data-field="date"
                    >
                      <InputLabel shrink={false}>
                        Date of Visit{" "}
                        <span style={{ color: "#D32F2F" }}> *</span>
                      </InputLabel>
                      <DateTimePicker
                        value={date ? new Date(date) : null}
                        onChange={setDate}
                        format="dd-MM-yyyy HH:mm"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            error={validationErrors.some(
                              (e) => e.field === "date"
                            )}
                          />
                        )}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <ClockIcon sx={{ color: "text.secondary" }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <FieldError errors={validationErrors} field="date" />
                    </FormControl>
                  </LocalizationProvider>
                </Grid>

                {/* <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                  <InputLabel shrink={false}>Time of Visit*</InputLabel>
                    <TextField
                      // label=""
                      placeholder="HH:MM"
                      value={timeOfVisit} // Bind the extracted time here
                      onChange={(e) => setTimeOfVisit(e.target.value)} // Allow manual update
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <ClockIcon sx={{ color: "text.secondary" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid> */}
              </Grid>
            </CardContent>
          </Card>
        </Stack>
      </Card>

      {/* <Box data-field="staffMemberDesc">
        <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
          Team Member<span style={{ color: "#D32F2F" }}> *</span>
        </Typography>

        <TextareaAutosize
          minRows={5}
          placeholder="Write Here..."
          value={staffMemberDesc}
          onChange={(e) => setStaffMemberDesc(e.target.value)}
          className={`summeryBox ${
            validationErrors.some((e) => e.field === "staffMemberDesc")
              ? "error-border"
              : ""
          }`}
          style={{
            borderColor: validationErrors.some(
              (e) => e.field === "staffMemberDesc"
            )
              ? "#d32f2f"
              : undefined,
            borderRadius: "10px",
          }}
        />
        <FieldError errors={validationErrors} field="staffMemberDesc" />
      </Box> */}

      {addOnQuestionsData.length !== 0 && (
        <Box>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
            Additional Questions
          </Typography>
          <Grid container spacing={3}>
            {addOnQuestionsData.map((addOnQuestion) => (
              <Grid
                sx={{ width: "50%" }}
                item
                xs={12}
                md={6}
                key={addOnQuestion.reportAddOnQuestionId}
                data-field={`addOnQuestionField_${addOnQuestion.reportAddOnQuestionId}`}
              >
                <label>
                  {addOnQuestion.addOnQuestionText}{" "}
                  <span style={{ color: "#D32F2F" }}> *</span>
                </label>

                {addOnQuestion.addOnQuestionType === "text" && (
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={addOnQuestions[addOnQuestion.reportAddOnQuestionId] || ""}
                    name={`addOnQuestionField_${addOnQuestion.reportAddOnQuestionId}`}
                    onChange={(e) =>
                      onAddOnQuestionChange(addOnQuestion.reportAddOnQuestionId.toString(), e.target.value)
                    }
                    placeholder="Enter text"
                  />
                )}

                {addOnQuestion.addOnQuestionType === "time" && (
                  <TextField
                    fullWidth
                    variant="outlined"
                    name={`addOnQuestionField_${addOnQuestion.reportAddOnQuestionId}`}
                    value={addOnQuestions[addOnQuestion.reportAddOnQuestionId] || ""}
                    onChange={(e) =>
                      onAddOnQuestionChange(addOnQuestion.reportAddOnQuestionId.toString(), e.target.value)
                    }
                    type="time"
                  />
                )}

                {addOnQuestion.addOnQuestionType === "longText" && (
                  <TextareaAutosize
                    minRows={4}
                    placeholder="Enter detailed text"
                    style={{ width: '100%', padding: '16.5px 14px', borderRadius: 4, border: "1px solid #ccc" }}
                    name={`addOnQuestionField_${addOnQuestion.reportAddOnQuestionId}`}
                    value={addOnQuestions[addOnQuestion.reportAddOnQuestionId] || ""}
                    onChange={(e) =>
                      onAddOnQuestionChange(addOnQuestion.reportAddOnQuestionId.toString(), e.target.value)
                    }
                  />
                )}

                <FieldError
                  errors={validationErrors}
                  field={`addOnQuestionField_${addOnQuestion.reportAddOnQuestionId}`}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {briefJson.length !== 0 && (
        <Box>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
            Summary
          </Typography>
          <Grid container spacing={3} sx={{justifyContent: "space-between"}}>
            {briefJson.map((rop) => (
              <Grid
                item
                xs={12}
                md={6}
                sx={{minWidth: "48%"}}
                key={rop.reportBriefJsonId}
                data-field={`textInput_${rop.reportBriefJsonId}`}
              >
                <label>
                  {rop.briefJsonTitle}{" "}
                  <span style={{ color: "#D32F2F" }}> *</span>
                </label>
                <TextareaAutosize
                  minRows={5}
                  placeholder={rop.briefJsonDescription} // Use dynamic placeholder
                  value={textInputs[rop.reportBriefJsonId] || ""} // Ensure correct binding
                  onChange={(e) =>
                    onTextChange(rop.reportBriefJsonId.toString(), e.target.value)
                  }
                  className={`summeryBox ${
                    validationErrors.some(
                      (e) => e.field === `textInput_${rop.reportBriefJsonId}`
                    )
                      ? "error-border"
                      : ""
                  }`}
                  style={{
                    borderColor: validationErrors.some(
                      (e) => e.field === `textInput_${rop.reportBriefJsonId}`
                    )
                      ? "#d32f2f"
                      : undefined,
                    borderRadius: "10px",
                  }}
                />
                <FieldError
                  errors={validationErrors}
                  field={`textInput_${rop.reportBriefJsonId}`}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      <Divider sx={{ mt: 4, borderBottom: "2px solid #ADB792" }} />
    </Stack>
  );
};

export default RateYourExperience;
